<!-- eslint-disable max-len -->
<template>
  <div v-if="billingRecord">
    <!-- Header -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-receipt</v-icon>
        Billing Record Details
        <v-spacer />
        <v-chip
          :color="statusColor"
          text-color="white"
          class="mr-2"
        >
          <v-icon left small>{{ statusIcon }}</v-icon>
          {{ statusText }}
        </v-chip>
        <v-btn
          color="warning"
          class="mr-2"
          @click="editRecord"
        >
          <v-icon left>mdi-pencil</v-icon>
          Edit
        </v-btn>
        <v-btn
          color="primary"
          @click="exportRecord"
        >
          <v-icon left>mdi-download</v-icon>
          Export
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <div class="text-subtitle-2">Record ID</div>
            <div class="text-h6">{{ billingRecord._id || billingRecord.id }}</div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="text-subtitle-2">Created</div>
            <div class="text-h6">{{ formatDateTime(billingRecord.createdAt) }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Billing Period Information -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1 primary--text">
        <v-icon left>mdi-calendar-range</v-icon>
        Billing Period Information
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="primary">mdi-calendar-start</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Start Date</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(billingRecord.billingPeriod.startDate) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-col>

          <v-col cols="12" md="4">
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="primary">mdi-calendar-end</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>End Date</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(billingRecord.billingPeriod.endDate) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-col>

          <v-col cols="12" md="4">
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="info">mdi-calendar-clock</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Duration</v-list-item-title>
                <v-list-item-subtitle>{{ billingDuration }} days</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Consumption Data -->
    <v-card class="mb-4 consumption-card">
      <v-card-title class="subtitle-1 success--text compact-card-title">
        <v-icon left>mdi-lightning-bolt</v-icon>
        Consumption Data
      </v-card-title>

      <v-card-text class="compact-card-text">
        <v-row dense>
          <v-col v-if="showInitialReading" cols="12" :md="consumptionMetricColumnWidth">
            <div class="compact-metric">
              <v-icon color="success">mdi-gauge-empty</v-icon>
              <div>
                <div class="text-caption grey--text">Initial Reading</div>
                <div class="text-subtitle-1 success--text">{{ formatNumber(billingRecord.consumptionData.initialReading) }} kWh</div>
              </div>
            </div>
          </v-col>

          <v-col v-if="showFinalReading" cols="12" :md="consumptionMetricColumnWidth">
            <div class="compact-metric">
              <v-icon color="success">mdi-gauge-full</v-icon>
              <div>
                <div class="text-caption grey--text">Final Reading</div>
                <div class="text-subtitle-1 success--text">{{ formatNumber(billingRecord.consumptionData.finalReading) }} kWh</div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" :md="consumptionMetricColumnWidth">
            <div class="compact-metric">
              <v-icon color="primary">mdi-lightning-bolt</v-icon>
              <div>
                <div class="text-caption grey--text">Calculated Consumption</div>
                <div class="text-subtitle-1 primary--text">{{ formatNumber(billingRecord.consumptionData.totalConsumption) }} kWh</div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" :md="consumptionMetricColumnWidth">
            <div class="compact-metric">
              <v-icon color="orange">mdi-file-document</v-icon>
              <div>
                <div class="text-caption grey--text">Actual kWh from Bill</div>
                <div class="text-subtitle-1 orange--text">{{ formatNumber(billingRecord.actualKwhFromBill || 0) }} kWh</div>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row dense class="mt-1">
          <v-col cols="12" md="6">
            <div class="compact-metric">
              <v-icon color="info">mdi-flash</v-icon>
              <div>
                <div class="text-caption grey--text">{{ demandLabel }}</div>
                <div class="text-subtitle-1 info--text">{{ formatNumber(displayKvaDemand) }}</div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="compact-metric">
              <v-icon color="purple">mdi-calculator</v-icon>
              <div>
                <div class="text-caption grey--text">Per Unit Charge</div>
                <div class="text-subtitle-1 purple--text">{{ formatNumber(perUnitCharge, 2) }} {{ $currencySymbol }}/kWh</div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Average Daily Consumption -->
        <v-row dense class="mt-1">
          <v-col cols="12">
            <v-alert
              type="info"
              outlined
              dense
              class="mb-0"
            >
              Average Daily Consumption:
              <strong>{{ averageDailyConsumption }} kWh/day</strong>
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Bill Breakdown -->
    <BillBreakdownDisplay :bill-data="billingRecord" />

    <!-- Actions -->
    <v-card class="mt-2">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-cog</v-icon>
        Actions
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" class="text-center">
            <v-btn
              color="primary"
              class="mr-2"
              @click="exportRecord"
            >
              <v-icon left>mdi-download</v-icon>
              Export Record
            </v-btn>

            <v-btn
              color="info"
              class="mr-2"
              @click="printRecord"
            >
              <v-icon left>mdi-printer</v-icon>
              Print
            </v-btn>

            <v-btn
              color="secondary"
              @click="goBack"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              Back to List
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>

  <!-- Loading State -->
  <div v-else-if="loading" class="text-center pa-8">
    <v-progress-circular
      indeterminate
      color="primary"
      size="64"
    />
    <div class="text-h6 mt-4">Loading billing record...</div>
  </div>

  <!-- Error State -->
  <div v-else class="text-center pa-8">
    <v-icon size="64" color="error">mdi-alert-circle</v-icon>
    <div class="text-h6 mt-4">Billing record not found</div>
    <div class="text-body-1 grey--text">The requested billing record could not be loaded.</div>
    <v-btn
      color="primary"
      class="mt-4"
      @click="goBack"
    >
      <v-icon left>mdi-arrow-left</v-icon>
      Back to List
    </v-btn>
  </div>
</template>

<script>
/* eslint-disable max-len */
import { mapGetters, mapActions } from 'vuex';
import { ExportService } from '../../services/exportService';
import BillBreakdownDisplay from './BillBreakdownDisplay.vue';
import costCategoryMixin from '../../mixins/costCategoryMixin';
import { resolveBillingMode, isTimeOfUseBillingMode } from '../../utils/billingMode';
import { calculateTimeOfUseTotalCalculated } from '../../utils/timeOfUseBilling';
import appConfig from '../../config/appConfig';

export default {
  name: 'BillingDetail',
  mixins: [costCategoryMixin],

  components: {
    BillBreakdownDisplay,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters('billing', ['loading']),

    billingRecord() {
      const { currentBill } = this.$store.state.billing;
      if (currentBill && (currentBill.id === this.id || currentBill._id === this.id)) {
        return currentBill;
      }

      const recordFromList = this.$store.getters['billing/getBillingRecordById'](this.id);
      if (recordFromList) {
        return recordFromList;
      }

      return null;
    },

    billingDuration() {
      if (!this.billingRecord) return 0;
      const start = new Date(this.billingRecord.billingPeriod.startDate);
      const end = new Date(this.billingRecord.billingPeriod.endDate);
      const diffTime = Math.abs(end - start);
      return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    },

    averageDailyConsumption() {
      if (!this.billingRecord || this.billingDuration === 0) return 0;
      return Math.round(this.billingRecord.consumptionData.totalConsumption / this.billingDuration);
    },

    showInitialReading() {
      return (parseFloat(this.billingRecord?.consumptionData?.initialReading) || 0) > 0;
    },

    showFinalReading() {
      return (parseFloat(this.billingRecord?.consumptionData?.finalReading) || 0) > 0;
    },

    consumptionMetricColumnWidth() {
      const visibleMetricCount = 2
        + (this.showInitialReading ? 1 : 0)
        + (this.showFinalReading ? 1 : 0);

      return 12 / visibleMetricCount;
    },

    displayedCalculatedAmount() {
      if (!this.billingRecord) return 0;
      if (!this.shouldUseBillingUnitCost) {
        return parseFloat(this.billingRecord.calculatedCharges?.totalCalculated) || 0;
      }

      return calculateTimeOfUseTotalCalculated(this.billingRecord);
    },

    billingMode() {
      return resolveBillingMode({
        activeCategories: this.costCategories,
        record: this.billingRecord,
      });
    },

    shouldUseBillingUnitCost() {
      return appConfig.getBillingCapabilities().useTouUnitCost === true
        && isTimeOfUseBillingMode(this.billingMode);
    },

    demandLabel() {
      return this.shouldUseBillingUnitCost ? 'TOU Demand Total' : 'kVA Demand';
    },

    displayKvaDemand() {
      if (!this.shouldUseBillingUnitCost) {
        return parseFloat(this.billingRecord?.kvaDemand) || 0;
      }

      const timeOfUseDemand = this.billingRecord?.timeOfUseDemand
        || this.billingRecord?.consumptionData?.demandBreakdown
        || {};
      const totalDemand = Object.values(timeOfUseDemand)
        .reduce((sum, line) => sum + (parseFloat(line?.kw) || 0), 0);

      return totalDemand || parseFloat(this.billingRecord?.kvaDemand) || 0;
    },

    variancePercentage() {
      if (!this.billingRecord || !this.displayedCalculatedAmount) return '0.00';
      const variance = Math.abs(this.billingRecord.variance || 0);
      const percentage = (variance / this.displayedCalculatedAmount) * 100;
      return percentage.toFixed(2);
    },

    varianceColor() {
      const percentage = parseFloat(this.variancePercentage);
      if (percentage < 1) return 'success';
      if (percentage <= 5) return 'warning';
      return 'error';
    },

    statusColor() {
      const percentage = parseFloat(this.variancePercentage);
      if (percentage < 1) return 'success';
      if (percentage <= 5) return 'warning';
      return 'error';
    },

    statusIcon() {
      const percentage = parseFloat(this.variancePercentage);
      if (percentage < 1) return 'mdi-check-circle';
      if (percentage <= 5) return 'mdi-alert';
      return 'mdi-alert-circle';
    },

    statusText() {
      const percentage = parseFloat(this.variancePercentage);
      if (percentage < 1) return 'Excellent Match';
      if (percentage <= 5) return 'Acceptable Variance';
      return 'Significant Variance';
    },

    perUnitCharge() {
      if (!this.billingRecord || !this.billingRecord.consumptionData.totalConsumption) return 0;
      return this.billingRecord.actualBillAmount / this.billingRecord.consumptionData.totalConsumption;
    },
  },

  async created() {
    // Load the billing record if not already loaded
    await this.loadBillingRecord();
  },

  methods: {
    ...mapActions('billing', ['fetchBillingRecordForEdit']),

    async loadBillingRecord() {
      try {
        await this.fetchBillingRecordForEdit(this.id);
      } catch (error) {
        console.error('Error loading billing record:', error);
      }
    },

    exportRecord() {
      if (!this.billingRecord) {
        this.$emit('show-message', {
          type: 'error',
          message: 'No billing record data available for export',
        });
        return;
      }

      try {
        // Export the individual record to CSV
        ExportService.exportRecordToCSV(this.billingRecord);

        this.$emit('show-message', {
          type: 'success',
          message: 'Billing record exported successfully',
        });
      } catch (error) {
        console.error('Error exporting record:', error);
        this.$emit('show-message', {
          type: 'error',
          message: `Failed to export billing record: ${error.message || 'Unknown error'}`,
        });
      }
    },

    editRecord() {
      const recordId = this.billingRecord._id || this.billingRecord.id;
      this.$router.push(`/billing-history/${recordId}/edit`);
    },

    printRecord() {
      if (!this.billingRecord) {
        this.$emit('show-message', {
          type: 'error',
          message: 'No billing record data available for printing',
        });
        return;
      }

      try {
        // Create a print-friendly version of the record
        const printContent = this.generatePrintContent();

        // Create a new window for printing
        const printWindow = window.open('', '_blank', 'width=800,height=600');

        if (!printWindow) {
          this.$emit('show-message', {
            type: 'error',
            message: 'Unable to open print window. Please check your browser popup settings.',
          });
          return;
        }

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Electricity Billing Record - ${this.billingRecord.id || this.billingRecord._id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
              margin-bottom: 20px;
            }
            .section {
              margin-bottom: 20px;
              page-break-inside: avoid;
            }
            .section-title {
              font-size: 16px;
              font-weight: bold;
              color: #1976d2;
              border-bottom: 1px solid #ddd;
              padding-bottom: 5px;
              margin-bottom: 10px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
              padding: 4px 0;
            }
            .info-label {
              font-weight: bold;
              width: 40%;
            }
            .info-value {
              width: 60%;
            }
            .charges-breakdown {
              background-color: #f5f5f5;
              padding: 15px;
              border-radius: 5px;
            }
            .variance-info {
              background-color: #ffffff;
              padding: 12px 16px;
              border-left: 4px solid #2196f3;
              margin-top: 10px;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
            }
            .variance-info.variance-success {
              border-left-color: #4caf50;
            }
            .variance-info.variance-warning {
              border-left-color: #ff9800;
            }
            .variance-info.variance-error {
              border-left-color: #f44336;
            }
            .variance-label {
              font-size: 11px;
              text-transform: uppercase;
              color: #757575;
              font-weight: 600;
              letter-spacing: 0.5px;
              margin-bottom: 2px;
            }
            .variance-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 16px;
              margin-bottom: 12px;
            }
            .variance-amount {
              font-size: 18px;
              font-weight: 700;
            }
            .variance-amount.positive { color: #f44336; }
            .variance-amount.negative { color: #ff9800; }
            .variance-amount.neutral  { color: #4caf50; }
            .variance-status-chip {
              display: inline-block;
              padding: 2px 10px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 600;
              color: #ffffff;
            }
            .chip-success { background-color: #4caf50; }
            .chip-warning { background-color: #ff9800; }
            .chip-error   { background-color: #f44336; }
            .notes {
              background-color: #fff3cd;
              padding: 10px;
              border-left: 4px solid #ffc107;
              margin-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);

        printWindow.document.close();

        // Wait for content to load then print
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      } catch (error) {
        console.error('Error printing record:', error);
        this.$emit('show-message', {
          type: 'error',
          message: 'Failed to print record. Please try again.',
        });
      }
    },

    generatePrintContent() {
      const record = this.billingRecord;

      if (!record) {
        return '<div class="error">No billing record data available for printing.</div>';
      }

      const currencySymbol = this.$currencySymbol || '';
      const calculatedCharges = record.calculatedCharges || {};
      const totalCalculated = this.displayedCalculatedAmount
        || parseFloat(calculatedCharges.totalCalculated)
        || 0;
      const chargeRows = Object.entries(calculatedCharges)
        .filter(([category, value]) => category !== 'totalCalculated' && typeof value === 'number')
        .map(([category, value]) => `
          <div class="info-row">
            <span class="info-label">${this.formatCostCategory(category)}:</span>
            <span class="info-value">${this.formatCurrency(value)} ${currencySymbol}</span>
          </div>
        `)
        .join('');

      return `
        <div class="header">
          <h1>Electricity Billing Record</h1>
          <h2>Record ID: ${record.id || record._id}</h2>
          <p>Generated on: ${new Date().toLocaleString()}</p>
        </div>

        <div class="section">
          <div class="section-title">Billing Period</div>
          <div class="info-row">
            <span class="info-label">Start Date:</span>
            <span class="info-value">${this.formatDate(record.billingPeriod.startDate)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">End Date:</span>
            <span class="info-value">${this.formatDate(record.billingPeriod.endDate)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Duration:</span>
            <span class="info-value">${this.billingDuration} days</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Consumption Data</div>
          ${this.showInitialReading
    ? `
          <div class="info-row">
            <span class="info-label">Initial Reading:</span>
            <span class="info-value">${record.consumptionData.initialReading} kWh</span>
          </div>
          `
    : ''}
          ${this.showFinalReading
    ? `
          <div class="info-row">
            <span class="info-label">Final Reading:</span>
            <span class="info-value">${record.consumptionData.finalReading} kWh</span>
          </div>
          `
    : ''}
          <div class="info-row">
            <span class="info-label">Total Consumption:</span>
            <span class="info-value">${record.consumptionData.totalConsumption} kWh</span>
          </div>
          <div class="info-row">
            <span class="info-label">${this.demandLabel}:</span>
            <span class="info-value">${this.displayKvaDemand} ${this.shouldUseBillingUnitCost ? 'kW' : 'kVA'}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Calculated Charges</div>
          <div class="charges-breakdown">
            ${chargeRows || `
              <div class="info-row">
                <span class="info-value">No charge breakdown available</span>
              </div>`}
            <div class="info-row" style="
              border-top: 2px solid #333;
              padding-top: 8px;
              margin-top: 8px;">
              <span class="info-label"><strong>Total Calculated:</strong></span>
              <span class="info-value">
                <strong>
                  ${this.formatCurrency(totalCalculated)} ${currencySymbol}
                </strong>
              </span>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Bill Comparison</div>
          <div class="variance-info ${this.getVarianceClass(
    record.variance, totalCalculated,
  )}">
            <div class="variance-grid">
              <div>
                <div class="variance-label">Calculated Amount</div>
                <div class="variance-amount">
                  ${this.formatCurrency(totalCalculated)} ${currencySymbol}
                </div>
              </div>
              <div>
                <div class="variance-label">Actual Amount</div>
                <div class="variance-amount">
                  ${this.formatCurrency(record.actualBillAmount)} ${currencySymbol}
                </div>
              </div>
              <div>
                <div class="variance-label">Variance</div>
                <div class="variance-amount ${this.getVarianceAmountClass(record.variance)}">
                  ${this.formatCurrency(Math.abs(record.variance))} ${currencySymbol}
                </div>
              </div>
            </div>
            <div>
              <span class="variance-label">Status: </span>
              <span class="variance-status-chip ${
  this.getVarianceChipClass(record.variance, totalCalculated)
}">
                ${this.getVarianceStatus(record.variance, totalCalculated)}
                (${this.variancePercentage}%)
              </span>
            </div>
          </div>
        </div>

        ${record.notes
    ? `
        <div class="section">
          <div class="section-title">Notes</div>
          <div class="notes">
            ${record.notes}
          </div>
        </div>
        `
    : ''}

        <div class="section">
          <div class="section-title">Record Information</div>
          <div class="info-row">
            <span class="info-label">Created By:</span>
            <span class="info-value">${record.createdBy || 'System'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Created At:</span>
            <span class="info-value">${this.formatDateTime(record.createdAt)}</span>
          </div>
          ${record.updatedAt
    ? `
          <div class="info-row">
            <span class="info-label">Last Updated:</span>
            <span class="info-value">${this.formatDateTime(record.updatedAt)}</span>
          </div>
          `
    : ''}
        </div>
      `;
    },

    getVarianceStatus(variance, calculatedAmount) {
      if (!calculatedAmount) return 'Unknown';
      const percentage = Math.abs((variance / calculatedAmount) * 100);
      if (percentage < 1) return 'Excellent Match';
      if (percentage <= 5) return 'Good Match';
      return 'Requires Review';
    },

    getVarianceClass(variance, calculatedAmount) {
      if (!calculatedAmount) return '';
      const percentage = Math.abs((variance / calculatedAmount) * 100);
      if (percentage < 1) return 'variance-success';
      if (percentage <= 5) return 'variance-warning';
      return 'variance-error';
    },

    getVarianceChipClass(variance, calculatedAmount) {
      if (!calculatedAmount) return 'chip-success';
      const percentage = Math.abs((variance / calculatedAmount) * 100);
      if (percentage < 1) return 'chip-success';
      if (percentage <= 5) return 'chip-warning';
      return 'chip-error';
    },

    getVarianceAmountClass(variance) {
      if (variance > 0) return 'positive';
      if (variance < 0) return 'negative';
      return 'neutral';
    },

    goBack() {
      this.$router.push('/billing-history');
    },

    // Formatting methods
    formatNumber(value) {
      return value ? value.toLocaleString() : '0';
    },

    formatCurrency(value) {
      return value ? value.toLocaleString() : '0';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },

    formatDateTime(date) {
      return new Date(date).toLocaleString();
    },

    calculateSubtotal(calculatedCharges) {
      if (!calculatedCharges) return 0;
      // Calculate subtotal from all base charges (non-percentage charges)
      return Object.entries(calculatedCharges).reduce((subtotal, [key, value]) => {
        const isCalculatedTotal = key === 'totalCalculated';
        const isNumber = typeof value === 'number';
        const isPercentageCharge = key.includes('VAT')
          || key.includes('REA')
          || key.includes('EWURA');

        if (isCalculatedTotal || !isNumber || isPercentageCharge) {
          return subtotal;
        }

        return subtotal + value;
      }, 0);
    },
  },
};
</script>

<style scoped>
.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.compact-card-title {
  min-height: 44px;
  padding: 10px 16px 4px;
}

.compact-card-text {
  padding: 8px 16px 12px;
}

.compact-metric {
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  display: flex;
  gap: 12px;
  min-height: 58px;
  padding: 8px 12px;
}
</style>
